import {Typography} from 'components/Typography';
import {formatPriceNumber} from 'core/helpers';
import {getEventDateAndTime} from 'modules/events/helpers';
import {IEvent, IWinery} from 'modules/events/types';
import Link from 'next/link';
import {FC, useMemo} from 'react';
import style from './style.module.scss';
import {ShieldListMobile} from 'modules/events/components/ShieldListMobile';
import {ShieldList} from 'modules/events/components/ShieldList';
import {ShieldTastingHall} from 'modules/events/components/ShieldTastingHall';
import {getMediaSrc} from 'core/helpers/media';

interface IProps {
    event: IEvent;
}

export const EventListCard: FC<IProps> = ({event}) => {
    const {drinkType, eventDate, eventTimeBegin, eventTimeEnd, price, title, previewImage, url, winery} = event;

    const previewImageUrl = previewImage?.url || '';
    const imageTitle = previewImage?.title || '';

    const formattedDate = useMemo(
        () => getEventDateAndTime(eventDate, eventTimeBegin, eventTimeEnd),
        [eventDate, eventTimeBegin, eventTimeEnd],
    );
    const formattedPrice = useMemo(() => formatPriceNumber(price), [price]);

    return (
        <div className={style.main} data-skeleton-item={true}>
            <ImageArea
                imageSrc={getMediaSrc(previewImageUrl, {width: 864})}
                imageTitle={imageTitle}
                url={url}
                drinkType={drinkType}
                hasDegustationHall={winery?.hasDegustationHall}
            />

            <ContentArea
                url={url}
                title={title}
                price={formattedPrice}
                date={formattedDate}
                wineryTitle={winery?.title}
            />

            {drinkType && drinkType.code && 'mix' !== drinkType.code && (
                <div className={style.mobileShieldsArea}>
                    <ShieldListMobile drinkType={drinkType} />
                </div>
            )}
        </div>
    );
};

interface IImageAreaProps {
    imageSrc: string;
    imageTitle: string;
    url: string;
    drinkType?: IEvent['drinkType'];
    hasDegustationHall?: IWinery['hasDegustationHall'];
}

const ImageArea: FC<IImageAreaProps> = ({imageSrc, imageTitle, url, drinkType, hasDegustationHall}) => (
    <Link className={style.imageLink} href={url}>
        <div className={style.imageArea}>
            <img alt={imageTitle} className={style.image} loading={'lazy'} src={imageSrc} />
            {drinkType && drinkType.code && 'mix' !== drinkType.code && (
                <div className={style.shieldsContainer}>
                    <ShieldList drinkType={drinkType} />
                </div>
            )}

            {hasDegustationHall && (
                <div className={style.tastingHallContainer}>
                    <ShieldTastingHall />
                </div>
            )}
        </div>
    </Link>
);

interface IContentAreaProps {
    url: string;
    title: string;
    price: string;
    date: string;
    wineryTitle?: IWinery['title'];
}

const ContentArea: FC<IContentAreaProps> = ({url, title, price, date, wineryTitle}) => (
    <div className={style.contentArea}>
        <Link href={url}>
            <Typography className={style.title} variant={'ch-title-24-b'}>
                {title}
            </Typography>
        </Link>
        <Typography variant={'price-24-b'}>{price}</Typography>
        <Typography variant={'label-14-r-fluid'}>{date}</Typography>
        {wineryTitle && <Typography variant={'label-14-r-fluid'}>{wineryTitle}</Typography>}
    </div>
);
